import {
  primarySamplingSchedule,
  type StrapiCustomerAddressMini,
  type StrapiSamplingScheduleRow,
} from '../../api/strapiCrm';

function asRecord(v: unknown): Record<string, unknown> | null {
  return v != null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function unwrapOne(v: unknown): Record<string, unknown> | null {
  const rec = asRecord(v);
  if (!rec) return null;
  if ('data' in rec && rec.data != null && !('documentId' in rec)) return unwrapOne(rec.data);
  return rec;
}

/** Group/header customer for sampling WO: valid if it has documentId or a name. */
export function customerAddressMiniFromUnknown(raw: unknown): StrapiCustomerAddressMini | null {
  const rec = unwrapOne(raw);
  if (!rec) return null;
  const documentId = String(rec.documentId ?? '').trim();
  const name = String(rec.name ?? '').trim();
  if (!documentId && !name) return null;
  return {
    documentId,
    customer_number: String(rec.customer_number ?? ''),
    name,
    address: rec.address != null ? String(rec.address) : null,
    city: rec.city != null ? String(rec.city) : null,
    province: rec.province != null ? String(rec.province) : null,
    postal_code: rec.postal_code != null ? String(rec.postal_code) : null,
    contact_name: rec.contact_name != null ? String(rec.contact_name) : null,
    job_title: rec.job_title != null ? String(rec.job_title) : null,
    email: rec.email != null ? String(rec.email) : null,
    work_email: rec.work_email != null ? String(rec.work_email) : null,
  };
}

/** Email pelanggan quotation: `email`, lalu `work_email`. Kosong jika keduanya tidak ada. */
export function quotationCustomerEmail(
  customer: { email?: string | null; work_email?: string | null } | null | undefined,
): string {
  const email = String(customer?.email ?? '').trim();
  if (email) return email;
  return String(customer?.work_email ?? '').trim();
}

function customerFromGroup(group: unknown): StrapiCustomerAddressMini | null {
  const rec = unwrapOne(group);
  return rec ? customerAddressMiniFromUnknown(rec.customer) : null;
}

function customerFromSamplingPlan(plan: unknown): StrapiCustomerAddressMini | null {
  const rec = unwrapOne(plan);
  if (!rec) return null;
  const fromGroup = customerFromGroup(rec.quotation_group);
  if (fromGroup) return fromGroup;
  const qsi = unwrapOne(rec.quotation_sample_information);
  return customerFromGroup(qsi?.quotation_group);
}

export type ResolveWorkOrderDisplayCustomerInput = {
  sampling_schedules?: StrapiSamplingScheduleRow[] | null;
  sampling_schedule?: StrapiSamplingScheduleRow | null;
  quotation?: { customer?: StrapiCustomerAddressMini | null } | null;
  samplingPlans?: unknown[] | null;
};

/**
 * Customer for sampling tab Detail / alamat form.
 * Prefer the group assigned to the WO schedule, then sampling plans, then quotation header.
 */
export function resolveWorkOrderDisplayCustomer(
  input: ResolveWorkOrderDisplayCustomerInput,
): StrapiCustomerAddressMini | null {
  const schedules = input.sampling_schedules ?? [];
  const primary = primarySamplingSchedule(schedules, input.sampling_schedule);
  const fromPrimary = customerFromGroup(primary?.quotation_group);
  if (fromPrimary) return fromPrimary;

  for (const schedule of schedules) {
    const fromSchedule = customerFromGroup(schedule.quotation_group);
    if (fromSchedule) return fromSchedule;
  }

  for (const plan of input.samplingPlans ?? []) {
    const fromPlan = customerFromSamplingPlan(plan);
    if (fromPlan) return fromPlan;
  }

  return customerAddressMiniFromUnknown(input.quotation?.customer);
}
